<script setup lang="ts">
const appConfig = useAppConfig();
const title = `お問い合わせ | ${appConfig.SITE_NAME}`;
useHead({
  title,
  meta: [
    { name: "description", content: "DOGFUL(ドッグフル)へのお問い合わせ" },
    { name: "og:title", content: title },
    { name: "og:description", content: "DOGFUL(ドッグフル)へのお問い合わせ" },
    { name: "twitter:title", content: title },
    { name: "robots", content: "noindex" },
  ],
});

// 種類の slug は backend / app / CMS と共有する（→ お問い合わせ API 契約）。
const categories = [
  { title: "取材依頼", value: "coverage_request" },
  { title: "掲載依頼", value: "listing_request" },
  { title: "掲載情報の訂正", value: "listing_correction" },
  { title: "不具合の報告", value: "bug_report" },
  { title: "その他", value: "other" },
];

const form = ref();
const name = ref("");
const email = ref("");
const tel = ref("");
const category = ref<string | null>(null);
const body = ref("");
const submitting = ref(false);
const result = ref<"success" | "error" | null>(null);

const required = (v: string) => !!v || "必須項目です";
const emailRule = (v: string) =>
  /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v) || "メールアドレスの形式が正しくありません";

const submit = async () => {
  const { valid } = await form.value.validate();
  if (!valid) return;

  submitting.value = true;
  result.value = null;
  try {
    await apiFetch("/inquiries", {
      method: "POST",
      body: {
        inquiry: {
          name: name.value,
          email: email.value,
          tel: tel.value || undefined,
          category: category.value,
          body: body.value,
        },
      },
    });
    result.value = "success";
    form.value.reset();
    category.value = null;
  } catch {
    result.value = "error";
  } finally {
    submitting.value = false;
  }
};
</script>

<template lang="pug">
v-container
  v-row(align="center" justify="center")
    v-col(cols="12").text-center
      h2 CONTACT
  v-row(align="center" justify="center")
    v-col(cols="12").text-center
      h3 お問い合わせ
  v-row(justify="center")
    v-col(cols="12" md="8" lg="6").form-col
      v-alert(
        v-if="result === 'success'"
        type="success"
        variant="tonal"
        class="mb-6"
      ) お問い合わせを送信しました。担当者より順次ご連絡いたします。
      v-alert(
        v-else-if="result === 'error'"
        type="error"
        variant="tonal"
        class="mb-6"
      ) 送信に失敗しました。時間をおいて再度お試しください。

      v-form(ref="form" @submit.prevent="submit")
        v-text-field(
          v-model="name"
          label="名前 *"
          :rules="[required]"
          variant="outlined"
        )
        v-text-field(
          v-model="email"
          label="メールアドレス *"
          type="email"
          :rules="[required, emailRule]"
          variant="outlined"
        )
        v-text-field(
          v-model="tel"
          label="電話番号（任意）"
          type="tel"
          variant="outlined"
        )
        v-select(
          v-model="category"
          label="お問い合わせの種類 *"
          :items="categories"
          :rules="[required]"
          variant="outlined"
        )
        v-textarea(
          v-model="body"
          label="お問い合わせ内容 *"
          :rules="[required]"
          variant="outlined"
          rows="6"
        )
        v-btn(
          type="submit"
          color="primary"
          size="large"
          block
          :loading="submitting"
        ) 送信する
</template>

<style lang="scss" scoped>
h2 {
  width: 100%;
  height: 52px;
  font-weight: 600;
  font-size: 34px;
  letter-spacing: 0.16px;
  background-color: #fff;
  color: #737373;
  margin: auto;
}

h3 {
  background-color: #f5f5f5;
  width: 315px;
  height: 47px;
  padding-top: 15px;
  color: #737373;
  font-size: 16px;
  letter-spacing: -0.56px;
  margin: auto;
  font-weight: 700;
}

.form-col {
  margin: 48px auto;
}
</style>
